// R58-L-09 — Promote-without-mapper batch modal (OQ-16 b).
//
// Opens when R58-L-08's `bulkPromote` response contains a non-empty
// `needsManual` array. Lists the unmapped findings as rows; the analyst
// picks a single batch-default class + severity at the top of the modal,
// and may per-row override class / severity / chord%.
//
// On Apply we merge batch defaults + per-row overrides client-side and
// hit `bulkPromoteAiFindingsManual` with one fully-resolved row per
// finding. On Cancel we close — the AI findings stay pending.

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { DEFECT_SEVERITIES, type DefectSeverity } from "@aeriosiq/types";
import { useCataloguesStore } from "../../catalogues/cataloguesStore.js";
import { useEffectiveClassModel } from "../../catalogues/useEffectiveClassModel.js";
import { gradeToSeverity } from "../../defects/severityDisplay.js";
import { useDataAcquisitionDetailQuery } from "../queries.js";
import { usePromoteManual } from "./useBulkPromote.js";

export interface NeedsManualRow {
  /** AI-finding uuid the modal will promote. */
  findingId: string;
  /** AI model's `mlClass` label — shown read-only as context. */
  mlClass: string;
  /**
   * Side the AI finding's bbox sits on (LE / TE / SS / PS). When the row's
   * effective side is SS or PS, chordPercent becomes required (BR-032 AC6).
   * Pass null when unknown — the modal then requires chord on every row
   * (conservative).
   */
  side?: "LE" | "TE" | "SS" | "PS" | null;
}

export interface PromoteBatchModalProps {
  /** When true the dialog is mounted + visible. */
  open: boolean;
  /** Closing the dialog (Cancel, ESC, backdrop click). */
  onClose: () => void;
  /** Findings to render — comes from L-08's `needsManual` response. */
  rows: NeedsManualRow[];
  /** Forwarded so the manual-promote mutation can invalidate caches. */
  inspectionId: string;
  /** Called after a successful Apply with the BE's promoted[] array. */
  onPromoted?: (promoted: { findingId: string; defectId: string }[]) => void;
}

interface RowOverride {
  /** Toggle: per-row class override is active. */
  overrideClass: boolean;
  /** Per-row class entry id (only used when overrideClass=true). */
  classId: string | null;
  /** Toggle: per-row severity override is active. */
  overrideSeverity: boolean;
  /**
   * Per-row severity OPTION VALUE (only used when overrideSeverity=true).
   * UX Q6 (8.0.0): with a resolved class model this is the grade number as
   * a string ("1".."N"); on the legacy fallback it is the contract enum.
   * `toEnum` maps it onto the (unchanged) request-side enum at submit.
   */
  severity: string | null;
  /** Chord% — required for SS/PS rows. */
  chordPercent: number | null;
  /** Optional analyst note for THIS row only. */
  notes: string;
}

/**
 * One option of the modal's severity selects. UX Q6 (severity story,
 * 8.0.0): the class-model's own grades are the canonical vocabulary —
 * `label` is the grade number (what the DefectDrawer's radios show) and
 * `toEnum` maps it RELATIVE to the model onto the unchanged request-side
 * enum (mirror of the gateway's read-side mapping). Without a resolvable
 * model (legacy/test mounts) the options fall back to the contract enum.
 */
interface SeverityOption {
  value: string;
  label: string;
  toEnum: DefectSeverity;
  color?: string | null;
}

const EMPTY_OVERRIDE: RowOverride = {
  overrideClass: false,
  classId: null,
  overrideSeverity: false,
  severity: null,
  chordPercent: null,
  notes: "",
};

function requiresChord(side: NeedsManualRow["side"]): boolean {
  // Conservative: require chord when side is SS/PS, OR unknown (null).
  // FE callers that know the side can suppress the requirement; ignorance
  // → ask the analyst.
  return side === "SS" || side === "PS" || side == null;
}

export function PromoteBatchModal({
  open,
  onClose,
  rows,
  inspectionId,
  onPromoted,
}: PromoteBatchModalProps): React.ReactElement {
  // Active catalogue entries — populated by CataloguesBootstrap; we read
  // straight off the store rather than fetching on open (avoid double
  // network on a hot path).
  const activeCatalogueId = useCataloguesStore((s) => s.activeCatalogueId);
  const entriesByCatalogue = useCataloguesStore((s) => s.entries);
  const entries = useMemo(
    () =>
      activeCatalogueId ? (entriesByCatalogue[activeCatalogueId] ?? []) : [],
    [activeCatalogueId, entriesByCatalogue],
  );

  // UX Q6 (severity story, 8.0.0) — resolve the DA's effective class model
  // (the DA detail is already cached on the analysis page; the class-models
  // list is session-cached reference data) so the severity selects speak
  // the model's own grade vocabulary instead of the derived contract enum.
  const detailQuery = useDataAcquisitionDetailQuery(inspectionId);
  const effectiveClassModel = useEffectiveClassModel(
    detailQuery.data?.activeClassModelId ?? null,
  );
  const severityOptions = useMemo<SeverityOption[]>(() => {
    if (effectiveClassModel.grades.length > 0) {
      const classCount = effectiveClassModel.classModel?.classCount ?? null;
      return effectiveClassModel.grades.map((g) => ({
        value: String(g.classCategory),
        label: String(g.classCategory),
        // grades are 1..N so gradeToSeverity never returns null here; the
        // ?? keeps the type closed without a non-null assertion.
        toEnum: gradeToSeverity(g.classCategory, classCount) ?? "info",
        color: g.colorCode ?? null,
      }));
    }
    // Legacy fallback — no class-model context: contract-enum vocabulary.
    return DEFECT_SEVERITIES.map((s) => ({ value: s, label: s, toEnum: s }));
  }, [effectiveClassModel]);
  const gradeVocabulary = effectiveClassModel.grades.length > 0;
  // Grade default = the model's LOWEST grade (the drawer's prefill
  // convention) — never a fabricated mid-tier. The enum fallback keeps the
  // pre-8.0.0 "medium" default.
  const defaultSeverityValue = gradeVocabulary
    ? (severityOptions[0]?.value ?? "medium")
    : "medium";

  // Batch defaults at the top of the modal.
  const [batchClassId, setBatchClassId] = useState<string | null>(null);
  const [batchSeverityValue, setBatchSeverityValue] =
    useState<string>(defaultSeverityValue);
  // The class model resolves asynchronously — when the option vocabulary
  // switches (enum → grades) reconcile a now-invalid selection back to the
  // default so the Select never holds an out-of-vocabulary value.
  useEffect(() => {
    if (!severityOptions.some((o) => o.value === batchSeverityValue)) {
      setBatchSeverityValue(defaultSeverityValue);
    }
  }, [severityOptions, batchSeverityValue, defaultSeverityValue]);
  /** Map an option value onto the request-side enum (request unchanged). */
  const toEnum = (value: string): DefectSeverity =>
    severityOptions.find((o) => o.value === value)?.toEnum ??
    (gradeVocabulary ? "info" : "medium");

  // Per-row override state, keyed by findingId.
  const [overrides, setOverrides] = useState<Record<string, RowOverride>>(() =>
    Object.fromEntries(rows.map((r) => [r.findingId, { ...EMPTY_OVERRIDE }])),
  );

  // Validation: collect rows missing required chord OR missing
  // (batch | row) class. The Apply button is disabled when any row is
  // invalid; each row also displays its own inline error.
  const errorsByRow = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of rows) {
      const ovr = overrides[row.findingId] ?? EMPTY_OVERRIDE;
      const effClassId = ovr.overrideClass ? ovr.classId : batchClassId;
      if (!effClassId) {
        out[row.findingId] = "Pick a defect class (batch default or per-row).";
        continue;
      }
      if (requiresChord(row.side) && ovr.chordPercent == null) {
        out[row.findingId] = "Chord% required for SS/PS findings.";
        continue;
      }
    }
    return out;
  }, [rows, overrides, batchClassId]);

  const hasErrors = Object.keys(errorsByRow).length > 0;

  const mutation = usePromoteManual(inspectionId);

  const onApply = (): void => {
    const items = rows.map((row) => {
      const ovr = overrides[row.findingId] ?? EMPTY_OVERRIDE;
      const rowClass = ovr.overrideClass ? ovr.classId : batchClassId;
      // Apply is disabled when any row's effective class is null (see
      // errorsByRow gating), so this fallback is unreachable in practice.
      const classId = rowClass ?? "";
      // UX Q6 — the UI spoke grade vocabulary; the wire still takes the
      // unchanged request-side enum, mapped relative to the class model.
      const severity: DefectSeverity = toEnum(
        ovr.overrideSeverity
          ? (ovr.severity ?? batchSeverityValue)
          : batchSeverityValue,
      );
      const item: {
        findingId: string;
        classId: string;
        severity: DefectSeverity;
        chordPercent?: number;
        notes?: string;
      } = { findingId: row.findingId, classId, severity };
      if (requiresChord(row.side) && ovr.chordPercent != null) {
        item.chordPercent = ovr.chordPercent;
      }
      if (ovr.notes.trim().length > 0) {
        item.notes = ovr.notes.trim();
      }
      return item;
    });

    mutation.mutate(
      { items },
      {
        onSuccess: (data) => {
          if (onPromoted) onPromoted(data.promoted);
          onClose();
        },
      },
    );
  };

  const setRow = (findingId: string, patch: Partial<RowOverride>): void => {
    setOverrides((prev) => ({
      ...prev,
      [findingId]: { ...(prev[findingId] ?? EMPTY_OVERRIDE), ...patch },
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="promote-batch-title"
      data-testid="promote-batch-modal"
    >
      <DialogTitle id="promote-batch-title">
        Promote {String(rows.length)} unmapped finding
        {rows.length === 1 ? "" : "s"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Batch defaults — top of modal */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Batch defaults (applied to every row unless overridden)
            </Typography>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                size="small"
                sx={{ flex: 1, minWidth: 240 }}
                options={entries}
                getOptionLabel={(e) => e.name.en}
                value={entries.find((e) => e.entryId === batchClassId) ?? null}
                onChange={(_, v) => {
                  setBatchClassId(v?.entryId ?? null);
                }}
                renderInput={(params) => {
                  const { inputProps, ...rest } = params;
                  const tfProps = {
                    ...rest,
                    label: "Defect class",
                    required: true,
                    slotProps: {
                      htmlInput: {
                        ...inputProps,
                        "data-testid": "promote-batch-default-class",
                      },
                    },
                  } as unknown as TextFieldProps;
                  return <TextField {...tfProps} />;
                }}
              />
              <TextField
                select
                size="small"
                label="Severity"
                value={batchSeverityValue}
                onChange={(e) => {
                  setBatchSeverityValue(e.target.value);
                }}
                sx={{ minWidth: 140 }}
                slotProps={{
                  htmlInput: {
                    "data-testid": "promote-batch-default-severity",
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              >
                {severityOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.color ? (
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          mr: 1,
                          display: "inline-block",
                          bgcolor: o.color,
                        }}
                      />
                    ) : null}
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          {/* Per-row list */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Findings ({String(rows.length)})
            </Typography>
            <Stack spacing={1.5} data-testid="promote-batch-rows">
              {rows.map((row) => {
                const ovr = overrides[row.findingId] ?? EMPTY_OVERRIDE;
                const err = errorsByRow[row.findingId];
                const needsChord = requiresChord(row.side);
                return (
                  <Box
                    key={row.findingId}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: err ? "error.main" : "divider",
                      borderRadius: 1,
                    }}
                    data-testid={`promote-batch-row-${row.findingId}`}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ minWidth: 140 }}>
                          AI class: <strong>{row.mlClass}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.findingId.slice(0, 8)}…
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Tooltip title="Use a different class for this row">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Switch
                              size="small"
                              checked={ovr.overrideClass}
                              onChange={(_, v) => {
                                setRow(row.findingId, { overrideClass: v });
                              }}
                              inputProps={
                                {
                                  "aria-label": "override class",
                                  "data-testid": `promote-batch-row-${row.findingId}-override-class-toggle`,
                                } as React.InputHTMLAttributes<HTMLInputElement>
                              }
                            />
                            <Typography variant="caption">
                              Override class
                            </Typography>
                          </Stack>
                        </Tooltip>
                        {ovr.overrideClass ? (
                          <Autocomplete
                            size="small"
                            sx={{ flex: 1, minWidth: 200 }}
                            options={entries}
                            getOptionLabel={(e) => e.name.en}
                            value={
                              entries.find((e) => e.entryId === ovr.classId) ??
                              null
                            }
                            onChange={(_, v) => {
                              setRow(row.findingId, {
                                classId: v?.entryId ?? null,
                              });
                            }}
                            renderInput={(params) => {
                              const { inputProps, ...rest } = params;
                              const tfProps = {
                                ...rest,
                                label: "Row class",
                                slotProps: {
                                  htmlInput: {
                                    ...inputProps,
                                    "data-testid": `promote-batch-row-${row.findingId}-class`,
                                  },
                                },
                              } as unknown as TextFieldProps;
                              return <TextField {...tfProps} />;
                            }}
                          />
                        ) : null}

                        <Tooltip title="Use a different severity for this row">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Switch
                              size="small"
                              checked={ovr.overrideSeverity}
                              onChange={(_, v) => {
                                setRow(row.findingId, { overrideSeverity: v });
                              }}
                              inputProps={
                                {
                                  "aria-label": "override severity",
                                  "data-testid": `promote-batch-row-${row.findingId}-override-severity-toggle`,
                                } as React.InputHTMLAttributes<HTMLInputElement>
                              }
                            />
                            <Typography variant="caption">
                              Override severity
                            </Typography>
                          </Stack>
                        </Tooltip>
                        {ovr.overrideSeverity ? (
                          <TextField
                            select
                            size="small"
                            label="Row severity"
                            value={ovr.severity ?? batchSeverityValue}
                            onChange={(e) => {
                              setRow(row.findingId, {
                                severity: e.target.value,
                              });
                            }}
                            sx={{ minWidth: 140 }}
                            slotProps={{
                              htmlInput: {
                                "data-testid": `promote-batch-row-${row.findingId}-severity`,
                              } as React.InputHTMLAttributes<HTMLInputElement>,
                            }}
                          >
                            {severityOptions.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.color ? (
                                  <Box
                                    component="span"
                                    aria-hidden="true"
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      mr: 1,
                                      display: "inline-block",
                                      bgcolor: o.color,
                                    }}
                                  />
                                ) : null}
                                {o.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : null}

                        {needsChord ? (
                          <TextField
                            size="small"
                            label="Chord%"
                            type="number"
                            required
                            value={ovr.chordPercent ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRow(row.findingId, {
                                chordPercent:
                                  v === "" ? null : Number.parseFloat(v),
                              });
                            }}
                            sx={{ width: 110 }}
                            slotProps={{
                              htmlInput: {
                                min: 0,
                                max: 100,
                                "data-testid": `promote-batch-row-${row.findingId}-chord`,
                              } as React.InputHTMLAttributes<HTMLInputElement>,
                            }}
                          />
                        ) : null}
                      </Stack>

                      {err ? (
                        <Alert
                          severity="warning"
                          sx={{ py: 0 }}
                          data-testid={`promote-batch-row-${row.findingId}-error`}
                        >
                          {err}
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="promote-batch-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={hasErrors || mutation.isPending}
          data-testid="promote-batch-apply"
        >
          {mutation.isPending ? "Saving…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
